import { computed, DestroyRef, EventEmitter, inject, Injectable, signal } from '@angular/core';
import { ModalDisplayRequest } from '@iupics-components/models/ui-request';
import { UICreatorService } from '@iupics-manager/managers/ui-creator/ui-creator.service';
import { UICreatorUtils } from '@iupics-manager/managers/ui-creator/utils/ui-creator.utils';
import { IupicsData, IupicsTableDataHeader } from '@iupics-manager/models/iupics-data';
import { IupicsColumnInfo } from '@iupics-manager/models/iupics_column_info';
import { ToolbarProcess } from '@iupics-manager/models/toolbar-item';
import { DataUtils } from '@iupics-util/tools/data.utils';
import { LogicEvaluator } from '@iupics-util/tools/logic-evaluator';
import { ReferenceUtils } from '@iupics-util/tools/reference.utils';
import {
  ColDef,
  DefaultFilterModelEntry,
  FilterModelOperator,
  FilterType,
  injectGridApiService,
  ValueFormatterFn,
} from '@iupics/apiz-grid';
import { IupicsMenuType } from '@web-desktop/models/menu-item-ui';
import { iif, map, Observable, of, shareReplay, Subject, switchMap, tap } from 'rxjs';

@Injectable()
export class ViewColumnsService {
  uuid = DataUtils.uuid();
  #uiCreator = inject(UICreatorService);
  #gridApi = injectGridApiService();
  #destroyRef = inject(DestroyRef);

  #view = new Subject<ViewParams>();

  #columns: IupicsColumnInfo[] = [];
  data: ViewColumnsData = undefined;

  #processes = signal<{ [key: string]: ToolbarProcess }>({});
  processes = computed(() => {
    return Object.values(this.#processes()).filter((process) => process.isDisplay);
  });

  updateModalDisplay = new EventEmitter<ModalDisplayRequest>();

  columns$ = this.#view.asObservable().pipe(
    switchMap((context) =>
      iif(
        () => !context.tabId || this.#columns.length > 0,
        of({ columns: this.#columns, context }),
        this.#uiCreator
          .getColumnInfos(context.tabId, context?.fromSpecific)
          .pipe(map((columns) => ({ columns, context })))
      )
    ),
    tap(({ columns }) => (this.#columns = columns))
  );

  colDefs$: Observable<ViewColumnsData> = this.columns$.pipe(
    map(({ columns, context }) => ({
      columns,
      columnsData: columns.reduce((acc, { fieldEntity: { field, details } }) => {
        if (
          (field?.Name || field?.name) &&
          (field?.Name ?? field.name).trim() !== '' &&
          (field.AD_Field_ID === -2 || field.IsDisplayed || field.IsKey)
        ) {
          acc.push({ data: field, details });
        }

        return acc;
      }, []),
      context,
    })),
    map(
      // eslint-disable-next-line complexity
      ({
        columns,
        columnsData,
        context: { data, hasCheckBox, windowType, ctx, columnFormatter, shouldSetColDefs = false },
      }) => {
        let tableDataHeader!: IupicsTableDataHeader[];
        if (data?.columnsTableHeader) {
          tableDataHeader = [...data.columnsTableHeader];
          // change columnHeader depending on issotrx
          if (data.columnsDetails && windowType !== IupicsMenuType.FORM) {
            if (windowType === IupicsMenuType.INFO_WINDOW) {
              columns.forEach((colInfo: IupicsColumnInfo) => {
                if (colInfo?.fieldEntity?.field?.ColumnSQL) {
                  colInfo.fieldEntity.ColumnName = colInfo?.fieldEntity?.field?.ColumnName;
                }
              });
            }
            if (ctx?.['IsSOTrx'] !== null && ctx?.['IsSOTrx'] === 'N') {
              tableDataHeader.map((colHeader: ColDef) => {
                if (data.columnsDetails.has(colHeader.field)) {
                  const colDetail = data.columnsDetails.get(colHeader.field);
                  if (colDetail?.field?.poName) {
                    colHeader.headerName = colDetail.field.poName;
                  }
                }
                colHeader.valueFormatter = columnFormatter;
                return colHeader;
              });
            }
          }
        }
        if (data?.urlSearch) {
          if (!tableDataHeader && data.searchColumns) {
            tableDataHeader = [];
            for (const el of data.searchColumns) {
              tableDataHeader.push(UICreatorUtils.transformFieldForGrid(el.field, true));
            }
            data.hasCheckbox = false;
          }
        }

        if (tableDataHeader?.[0]) {
          if (!tableDataHeader?.[0]?.checkboxSelection) {
            tableDataHeader.unshift({
              field: 'Data_UUID',
              headerName: '',
              sortable: false,
              enableRowGroup: false,
              width: 38,
              resizable: false,
              hide: data?.hasCheckbox !== undefined ? !data.hasCheckbox : !hasCheckBox,
            });

            tableDataHeader[0].checkboxSelection = (params) => {
              return data?.hasCheckbox ?? hasCheckBox ?? params.colDef.field === 'Data_UUID';
            };
          } else {
            tableDataHeader[0].hide = false;
          }

          for (const column of tableDataHeader) {
            if (column.field === 'Data_UUID') {
              continue;
            }
            column.sortable = true;
            column.resizable = true;
            column.valueFormatter = columnFormatter;
            if (data?.editorColumns?.length > 0 && data.editorColumns.indexOf(column.field) > -1) {
              column.sortable = false;
              // column.enablePivot = false;
              column.enableRowGroup = false;
            }
            const _column = columns.find((_col) => _col.fieldEntity.field.ColumnName === column.field);
            if (_column) {
              const componentName = UICreatorUtils.getComponentNameFromReference(
                _column.fieldEntity.field.AD_Reference_ID
              );
              column.type = componentName === 'InputNumberUiComponent' ? 'number' : 'text';
              if (componentName === 'ButtonUiComponent') {
                const { field } = _column.fieldEntity;
                // display / read-only rules are checked on the selected rows when the menu opens (menu-bar-ui)
                this.registerProcess({
                  componentUUID: DataUtils.uuid(),
                  isDisplay: true,
                  readOnly: false,
                  label: field.Name,
                  processId: field.AD_Process_ID,
                  tabId: field.AD_Tab_ID,
                  field: {
                    columnName: field.ColumnName,
                    fieldId: field.AD_Field_ID,
                    columnId: field.AD_Column_ID,
                    displayLogic: field.DisplayLogic,
                    readOnlyLogic: field.ReadOnlyLogic,
                    isReadOnly: field.IsReadOnly,
                    isUpdateable: field.IsUpdateable,
                    isAlwaysUpdatable: field.IsAlwaysUpdateable,
                  },
                });
              }
            }
          }
        }

        // Every selection column gets a default filter, typed from its reference
        const selectionColumns = columns
          .filter((col) => this.#hasDefaultFilter(col))
          .sort((a, b) => a.fieldEntity.field.SelectionSeqNo - b.fieldEntity.field.SelectionSeqNo);

        for (const selectionColumn of selectionColumns) {
          const i = tableDataHeader?.findIndex((_tdh) => _tdh.field === selectionColumn.fieldEntity.field.ColumnName);
          if (i !== undefined && i !== -1) {
            tableDataHeader[i].defaultFilters = [this.#getDefaultFilter(selectionColumn)];
          }
        }

        return {
          columns,
          columnsData,
          columnsTableHeader: this.getColumnsDisplayLogic(tableDataHeader ?? [], data, ctx, columnFormatter),
          shouldSetColDefs,
        };
      }
    ),
    tap((data) => {
      this.data = data;
      if (data.shouldSetColDefs) {
        this.#gridApi.updateOptions('columnDefs', data.columnsTableHeader);
      }
    }),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    })
  );

  constructor() {
    this.#destroyRef.onDestroy(() => {
      this.updateModalDisplay.complete();
      this.#view.complete();
    });
  }

  setView(view: ViewParams): void {
    this.#view.next(view);
  }

  getColumnsDisplayLogic(
    cols: IupicsTableDataHeader[],
    data: IupicsData,
    ctx: object,
    columnFormatter: ValueFormatterFn<any, any> | undefined
  ): IupicsTableDataHeader[] {
    cols = cols.map((col: IupicsTableDataHeader) => {
      col.valueFormatter = columnFormatter;
      return col;
    });

    if (data.columnsDisplayLogicMap && data.columnsDisplayLogicMap.size > 0) {
      return cols.filter((col) => {
        let returnCol = null;
        const displayLogic = data.columnsDisplayLogicMap.get(col.field);
        if (data.hiddenColumns !== undefined) {
          if (!data.hiddenColumns.includes(col.field)) {
            if (!displayLogic || displayLogic === '' || LogicEvaluator.evaluateLogic(ctx, displayLogic)) {
              returnCol = col;
            }
          } else {
            col.hide = true;
            returnCol = col;
          }
          return returnCol;
        } else {
          if (!displayLogic || displayLogic === '' || LogicEvaluator.evaluateLogic(ctx, displayLogic)) {
            returnCol = col;
          }

          return returnCol;
        }
      });
    } else {
      return cols;
    }
  }

  /**
   * A selection column gets a default filter only if the chip can edit it: FilterComponent builds the
   * input from the reference's component, and the value must match the filter type sent to the backend.
   */
  #hasDefaultFilter(column: IupicsColumnInfo): boolean {
    const field = column?.fieldEntity?.field;
    if (!field || !(field.isQueryCriteria ?? field.IsSelectionColumn)) {
      return false;
    }
    const referenceId = field.AD_Reference_ID;
    if (referenceId === BINARY_REFERENCE) {
      return false; // shown as a textarea, but a text search on binary data makes no sense
    }
    const componentName = ReferenceUtils.getComponentNameFromReference(referenceId);
    const filterType = column.filterType ?? ReferenceUtils.getFilterTypeFromReference(referenceId);
    const expectedFilterType = DEFAULT_FILTER_COMPONENTS[componentName];
    // e.g. PAttribute (35) is an autocomplete typed as text, Color (27) a color picker typed as set: skip them
    return expectedFilterType !== undefined && expectedFilterType.includes(filterType);
  }

  /** Default filter of a selection column: filter type from its reference, and the operator that fits that type. */
  #getDefaultFilter(column: IupicsColumnInfo): DefaultFilterModelEntry {
    const referenceId = column.fieldEntity.field.AD_Reference_ID;
    // same filter type as the column's filter panel, so the default filter matches its operators
    const filterType = column.filterType ?? ReferenceUtils.getFilterTypeFromReference(referenceId);
    // text: 'contains' (as before); number, date, time, list/table/search (set): 'equals'.
    // YN is typed as text, but 'contains' makes no sense on Y/N.
    const operator: FilterModelOperator =
      filterType === FilterType.TEXT && referenceId !== YES_NO_REFERENCE ? 'contains' : 'equals';
    return { filterType, operators: [operator] };
  }

  registerProcess(process: ToolbarProcess): void {
    this.#processes.update((processes) => ({ ...processes, [process.componentUUID]: process }));
  }

  updateProcess(uuid: string, partialProcess: Partial<ToolbarProcess>): void {
    this.#processes.update((processes) => {
      const process = processes[uuid];
      return { ...processes, [uuid]: { ...process, ...partialProcess } };
    });
  }
}

/** Display type YN (checkbox) */
const YES_NO_REFERENCE = 20;
/** Display type Binary / BLOB */
const BINARY_REFERENCE = 23;
/**
 * Components a default-filter chip can edit, with the filter type their value fits.
 * Anything else (Location, Button, Image, File, Comment, JSON, Icon, Color, unknown references) gets no default filter.
 */
const DEFAULT_FILTER_COMPONENTS: Record<string, FilterType[]> = {
  InputTextUiComponent: [FilterType.TEXT], // String, File path/name, URL
  InputTextareaUiComponent: [FilterType.TEXT], // Text, Memo, CLOB, long String (edited as a text input)
  InputTextHtmlUiComponent: [FilterType.TEXT], // Text HTML (edited as a text input)
  InputSwitchUiComponent: [FilterType.TEXT], // Yes-No: 'Y' / 'N'
  InputNumberUiComponent: [FilterType.NUMBER], // Integer, Amount, ID, Number, Quantity, Cost price
  CalendarUiComponent: [FilterType.DATE], // Date, Date+time
  InputTimeUiComponent: [FilterType.TIME], // Time
  AutocompleteUiComponent: [FilterType.SET], // List, Table, TableDir, Search, Account, Locator, Printer name
};

type ViewParams = {
  tabId: number;
  fromSpecific?: boolean;
  data?: IupicsData;
  ctx?: any;
  hasCheckBox?: boolean;
  windowType?: IupicsMenuType;
  columnFormatter?: (params: any) => string;
  shouldSetColDefs?: boolean;
};

type ViewColumnsData = {
  columns: IupicsColumnInfo[];
  columnsData: IupicsTableDataHeader[];
  columnsTableHeader: IupicsTableDataHeader[];
  shouldSetColDefs: boolean;
};
